import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { PackageCheck } from 'lucide-react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import { Button, Drawer, TextField } from '@shared/design-system/ui'
import { formatCEP } from '@shared/lib/format/masks'
import { useCepLookup } from '@shared/lib/address/use-cep-lookup'
import { onlyDigits } from '@shared/lib/validation/documents'
import type { AwardTier } from '../api/dashboard.contract'
import { drawerForm, formGrid, formGridFull } from './dashboard.css'

const awardAddressSchema = z.object({
  rua: z.string().min(3, 'Informe a rua.'),
  cidade: z.string().min(2, 'Informe a cidade.'),
  estado: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, 'Informe a UF.'),
  cep: z.string().refine((value) => onlyDigits(value).length === 8, 'CEP deve ter 8 dígitos.'),
})

type AwardAddressForm = z.infer<typeof awardAddressSchema>

const emptyAddress: AwardAddressForm = {
  rua: '',
  cidade: '',
  estado: '',
  cep: '',
}

export type RedeemAwardDrawerProps = {
  tier: AwardTier | null
  open: boolean
  isRequesting: boolean
  onClose: () => void
  onRequest: (metaId: string, endereco: Record<string, unknown>) => Promise<void>
}

export function RedeemAwardDrawer({
  tier,
  open,
  isRequesting,
  onClose,
  onRequest,
}: RedeemAwardDrawerProps) {
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setValue,
    control,
  } = useForm<AwardAddressForm>({
    resolver: zodResolver(awardAddressSchema),
    defaultValues: emptyAddress,
  })
  const cep = useWatch({ control, name: 'cep' })
  const cepLookup = useCepLookup(cep, { enabled: open })

  useEffect(() => {
    if (!open) {
      reset(emptyAddress)
    }
  }, [open, reset])

  useEffect(() => {
    if (!cepLookup.address) {
      return
    }

    const current = getValues()
    if (!current.rua && cepLookup.address.line1) {
      setValue('rua', cepLookup.address.line1, { shouldDirty: true, shouldValidate: true })
    }
    if (!current.cidade) {
      setValue('cidade', cepLookup.address.city, { shouldDirty: true, shouldValidate: true })
    }
    if (!current.estado) {
      setValue('estado', cepLookup.address.state, { shouldDirty: true, shouldValidate: true })
    }
  }, [cepLookup.address, getValues, setValue])

  const cepField = register('cep')
  const estadoField = register('estado')

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={tier ? `Solicitar ${tier.nome}` : 'Solicitar prêmio'}
      description="Confirme o endereço de entrega e acompanhe a validação do CEP."
      size="standard"
      variant="form"
      busy={isRequesting}
      dirty={isDirty}
      footer={
        <>
          <Button variant="secondary" disabled={isRequesting} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="redeem-award-form"
            disabled={isRequesting}
            leftIcon={<PackageCheck size={16} />}
          >
            {isRequesting ? 'Solicitando...' : 'Solicitar prêmio'}
          </Button>
        </>
      }
    >
      <form
        id="redeem-award-form"
        className={drawerForm}
        onSubmit={(event) => {
          void handleSubmit(async (values) => {
            if (!tier) {
              return
            }
            try {
              await onRequest(tier.id, {
                rua: values.rua,
                cidade: values.cidade,
                estado: values.estado.toUpperCase(),
                cep: onlyDigits(values.cep),
              })
              onClose()
            } catch {
              // A mutation apresenta o erro; manter o formulário aberto preserva os dados.
            }
          })(event)
        }}
      >
        <div className={formGrid}>
          <TextField
            className={formGridFull}
            label="CEP"
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={9}
            helperText={
              cepLookup.status === 'loading'
                ? 'Buscando endereço pelo CEP...'
                : cepLookup.status === 'filled'
                  ? 'Endereço preenchido pelo CEP.'
                  : 'Digite o CEP para preencher endereço, cidade e UF.'
            }
            error={
              errors.cep?.message ??
              (cepLookup.status === 'error'
                ? 'Não foi possível consultar o CEP. Preencha o endereço manualmente.'
                : undefined)
            }
            {...cepField}
            onChange={(event) => {
              void cepField.onChange(event)
              setValue('cep', formatCEP(event.target.value), { shouldValidate: true })
            }}
          />
          <TextField
            className={formGridFull}
            label="Rua"
            autoComplete="address-line1"
            error={errors.rua?.message}
            {...register('rua')}
          />
          <TextField
            label="Cidade"
            autoComplete="address-level2"
            error={errors.cidade?.message}
            {...register('cidade')}
          />
          <TextField
            label="UF"
            autoComplete="address-level1"
            maxLength={2}
            error={errors.estado?.message}
            {...estadoField}
            onChange={(event) => {
              void estadoField.onChange(event)
              setValue('estado', event.target.value.toUpperCase(), { shouldValidate: true })
            }}
          />
        </div>
      </form>
    </Drawer>
  )
}
